import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { Box, Button, Collapse, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { getProficiencyGroupGuidance } from './proficiencyGroupGuidance';

interface ProficiencyGroupGuidanceCardProps {
    groupName?: string | null;
}

const ProficiencyGroupGuidanceCard = ({ groupName }: ProficiencyGroupGuidanceCardProps) => {
    const guidance = getProficiencyGroupGuidance(groupName);
    const [expanded, setExpanded] = useState(false);
    const detailsId = guidance ? `proficiency-group-guidance-${guidance.name.toLowerCase()}-details` : undefined;

    useEffect(() => setExpanded(false), [guidance?.name]);

    if (!guidance)
        return null;

    return (
        <Paper
            component="aside"
            variant="outlined"
            aria-labelledby={`${detailsId}-title`}
            data-testid="proficiency-group-guidance"
            sx={{ flex: '0 0 auto', mx: 1, mt: 1, px: 1.5, py: 1, bgcolor: 'action.hover' }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id={`${detailsId}-title`} variant="subtitle2" fontWeight={750}>About {guidance.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{guidance.shortDefinition}</Typography>
                </Box>
                <Button
                    size="small"
                    color="inherit"
                    aria-expanded={expanded}
                    aria-controls={detailsId}
                    endIcon={<ExpandMoreRounded sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                    onClick={() => setExpanded(current => !current)}
                    sx={{ flex: '0 0 auto', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                    {expanded ? 'Hide guidance' : 'View guidance'}
                </Button>
            </Stack>
            <Collapse in={expanded} unmountOnExit>
                <Stack id={detailsId} spacing={1.25} sx={{ pt: 1.25 }}>
                    <GuidanceSection title="Why it matters" text={guidance.whyItMatters} />
                    <GuidanceSection title="High-scoring example" text={guidance.highScoringExample} />
                    <GuidanceSection title="Development example" text={guidance.developmentExample} />
                </Stack>
            </Collapse>
        </Paper>
    );
};

const GuidanceSection = ({ title, text }: { title: string; text: string }) => <Box>
    <Typography component="h4" variant="caption" fontWeight={750}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">{text}</Typography>
</Box>;

export default ProficiencyGroupGuidanceCard;
